import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Settings as SettingsIcon,
  Cpu,
  Image,
  Search,
  Printer,
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  Check,
} from 'lucide-react';
import {
  api,
  type Model,
  type PrintifyConfig,
  type PrintifyBlueprint,
  type PrintifyProvider,
} from '../api/client';

export default function Settings() {
  const queryClient = useQueryClient();
  // Track local edits - null means use server value
  const [textModelOverride, setTextModelOverride] = useState<string | null>(null);
  const [imageModelOverride, setImageModelOverride] = useState<string | null>(null);
  const [researchModelOverride, setResearchModelOverride] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch current settings from API
  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get(),
  });

  // Use override if user edited, otherwise use server value
  const textModel = textModelOverride ?? settingsData?.settings?.default_text_model ?? '';
  const imageModel = imageModelOverride ?? settingsData?.settings?.default_image_model ?? '';
  const researchModel =
    researchModelOverride ?? settingsData?.settings?.default_research_model ?? '';

  // Printify state
  const [showAddPreset, setShowAddPreset] = useState(false);
  const [productCategory, setProductCategory] = useState<'wall-art' | 'apparel' | 'drinkware'>(
    'wall-art'
  );
  const [selectedBlueprint, setSelectedBlueprint] = useState<PrintifyBlueprint | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<PrintifyProvider | null>(null);
  const [presetName, setPresetName] = useState('');
  const [presetProductModel, setPresetProductModel] = useState('');
  const [presetPrice, setPresetPrice] = useState('19.99');
  const [presetProductType, setPresetProductType] = useState('poster');
  const [isDefault, setIsDefault] = useState(false);

  const { data } = useQuery({
    queryKey: ['models'],
    queryFn: () => api.models.list(),
  });

  const { data: printifyConfigs, isLoading: configsLoading } = useQuery({
    queryKey: ['printify-configs'],
    queryFn: () => api.printify.configs(),
  });

  const { data: blueprintsData, isLoading: blueprintsLoading } = useQuery({
    queryKey: ['printify-blueprints', productCategory],
    queryFn: () => {
      if (productCategory === 'apparel') return api.printify.apparelBlueprints();
      if (productCategory === 'drinkware') return api.printify.drinkwareBlueprints();
      return api.printify.wallArtBlueprints();
    },
    enabled: showAddPreset,
  });

  const { data: providersData, isLoading: providersLoading } = useQuery({
    queryKey: ['printify-providers', selectedBlueprint?.id],
    queryFn: () => api.printify.providers(selectedBlueprint!.id),
    enabled: !!selectedBlueprint,
  });

  const saveConfigMutation = useMutation({
    mutationFn: () =>
      api.printify.saveConfig({
        name: presetName,
        blueprint_id: selectedBlueprint!.id,
        print_provider_id: selectedProvider!.id,
        product_type: presetProductType,
        product_model: presetProductModel || undefined,
        default_price: Math.round(parseFloat(presetPrice) * 100),
        is_default: isDefault,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printify-configs'] });
      resetAddForm();
    },
  });

  const deleteConfigMutation = useMutation({
    mutationFn: (id: number) => api.printify.deleteConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printify-configs'] });
    },
  });

  const resetAddForm = () => {
    setShowAddPreset(false);
    setProductCategory('wall-art');
    setSelectedBlueprint(null);
    setSelectedProvider(null);
    setPresetName('');
    setPresetProductModel('');
    setPresetPrice('19.99');
    setPresetProductType('poster');
    setIsDefault(false);
  };

  const models = data?.models ?? [];
  const textModels = models.filter((m: Model) => m.type === 'text');
  const imageModels = models.filter((m: Model) => m.type === 'image');

  // Mutation to save settings
  const saveSettingsMutation = useMutation({
    mutationFn: () =>
      api.settings.update({
        default_text_model: textModel,
        default_image_model: imageModel,
        default_research_model: researchModel,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      // Reset overrides so form uses server values
      setTextModelOverride(null);
      setImageModelOverride(null);
      setResearchModelOverride(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const handleSave = () => {
    saveSettingsMutation.mutate();
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-8">
        <SettingsIcon className="w-8 h-8 text-gray-600" />
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Research Model Selection */}
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <Search className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-semibold">Research Model</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Used for niche analysis (competition, demand, trends). Requires strong reasoning.
          </p>
          <select
            value={researchModel}
            onChange={(e) => setResearchModelOverride(e.target.value)}
            className="input"
            disabled={settingsLoading}
          >
            {settingsLoading ? (
              <option value="">Loading...</option>
            ) : (
              <option value="">Default (Claude 3.5 Sonnet)</option>
            )}
            {textModels.map((model: Model) => (
              <option key={model.id} value={model.id}>
                {model.name || model.id}
              </option>
            ))}
          </select>
        </div>

        {/* Text Model Selection */}
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <Cpu className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold">Text Generation Model</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Used for brainstorming concepts, SEO titles, tags, and descriptions.
          </p>
          <select
            value={textModel}
            onChange={(e) => setTextModelOverride(e.target.value)}
            className="input"
            disabled={settingsLoading}
          >
            {settingsLoading ? (
              <option value="">Loading...</option>
            ) : (
              textModels.map((model: Model) => (
                <option key={model.id} value={model.id}>
                  {model.name || model.id}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Image Model Selection */}
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <Image className="w-5 h-5 text-purple-500" />
            <h2 className="text-lg font-semibold">Image Generation Model</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Used for generating product artwork and designs.
          </p>
          <select
            value={imageModel}
            onChange={(e) => setImageModelOverride(e.target.value)}
            className="input"
            disabled={settingsLoading}
          >
            {settingsLoading ? (
              <option value="">Loading...</option>
            ) : (
              imageModels.map((model: Model) => (
                <option key={model.id} value={model.id}>
                  {model.name || model.id}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Printify POD Presets */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Printer className="w-5 h-5 text-purple-500" />
              <h2 className="text-lg font-semibold">Printify Product Presets</h2>
            </div>
            <button
              onClick={() => setShowAddPreset(!showAddPreset)}
              className="btn btn-secondary btn-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Preset
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Configure blueprint and print provider combinations for quick POD product creation.
          </p>

          {/* Add Preset Form */}
          {showAddPreset && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-4">
              <div>
                <label className="label">Preset Name</label>
                <input
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="e.g., Premium Poster 18x24"
                  className="input"
                />
              </div>

              <div>
                <label className="label">Product Model (optional)</label>
                <input
                  type="text"
                  value={presetProductModel}
                  onChange={(e) => setPresetProductModel(e.target.value)}
                  placeholder="e.g., Bella+Canvas 3001, Gildan 5000"
                  className="input"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Specific product model for display purposes
                </p>
              </div>

              <div>
                <label className="label">Product Category</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setProductCategory('wall-art');
                      setSelectedBlueprint(null);
                      setSelectedProvider(null);
                      setPresetProductType('poster');
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                      productCategory === 'wall-art'
                        ? 'bg-purple-100 border-purple-500 text-purple-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Wall Art
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setProductCategory('apparel');
                      setSelectedBlueprint(null);
                      setSelectedProvider(null);
                      setPresetProductType('tshirt');
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                      productCategory === 'apparel'
                        ? 'bg-purple-100 border-purple-500 text-purple-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Apparel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setProductCategory('drinkware');
                      setSelectedBlueprint(null);
                      setSelectedProvider(null);
                      setPresetProductType('mug');
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                      productCategory === 'drinkware'
                        ? 'bg-purple-100 border-purple-500 text-purple-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Drinkware
                  </button>
                </div>
              </div>

              <div>
                <label className="label">Blueprint (Product Type)</label>
                <div className="relative">
                  <select
                    value={selectedBlueprint?.id || ''}
                    onChange={(e) => {
                      const bp = blueprintsData?.blueprints.find(
                        (b) => b.id === parseInt(e.target.value)
                      );
                      setSelectedBlueprint(bp || null);
                      setSelectedProvider(null);
                    }}
                    className="input pr-8"
                    disabled={blueprintsLoading}
                  >
                    <option value="">
                      {blueprintsLoading ? 'Loading blueprints...' : 'Select a blueprint'}
                    </option>
                    {blueprintsData?.blueprints.slice(0, 50).map((bp) => (
                      <option key={bp.id} value={bp.id}>
                        {bp.title} ({bp.brand})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {selectedBlueprint && (
                <div>
                  <label className="label">Print Provider</label>
                  <div className="relative">
                    <select
                      value={selectedProvider?.id || ''}
                      onChange={(e) => {
                        const prov = providersData?.providers.find(
                          (p) => p.id === parseInt(e.target.value)
                        );
                        setSelectedProvider(prov || null);
                      }}
                      className="input pr-8"
                      disabled={providersLoading}
                    >
                      <option value="">
                        {providersLoading ? 'Loading providers...' : 'Select a provider'}
                      </option>
                      {providersData?.providers.map((prov) => (
                        <option key={prov.id} value={prov.id}>
                          {prov.title}
                          {prov.location?.country && ` (${prov.location.country})`}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Product Type</label>
                  <select
                    value={presetProductType}
                    onChange={(e) => setPresetProductType(e.target.value)}
                    className="input"
                  >
                    {productCategory === 'wall-art' && (
                      <>
                        <option value="poster">Poster</option>
                        <option value="canvas">Canvas</option>
                        <option value="framed">Framed Print</option>
                        <option value="metal">Metal Print</option>
                      </>
                    )}
                    {productCategory === 'apparel' && (
                      <>
                        <option value="tshirt">T-Shirt</option>
                        <option value="hoodie">Hoodie</option>
                        <option value="sweatshirt">Sweatshirt</option>
                        <option value="tank">Tank Top</option>
                        <option value="longsleeve">Long Sleeve</option>
                      </>
                    )}
                    {productCategory === 'drinkware' && (
                      <>
                        <option value="mug">Mug</option>
                        <option value="tumbler">Tumbler</option>
                        <option value="water-bottle">Water Bottle</option>
                        <option value="travel-mug">Travel Mug</option>
                        <option value="enamel-mug">Enamel Mug</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="label">Default Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={presetPrice}
                    onChange={(e) => setPresetPrice(e.target.value)}
                    className="input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-default"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="is-default" className="text-sm text-gray-600">
                  Set as default for this product type
                </label>
              </div>

              <div className="flex gap-2">
                <button onClick={resetAddForm} className="btn btn-secondary flex-1">
                  Cancel
                </button>
                <button
                  onClick={() => saveConfigMutation.mutate()}
                  disabled={
                    !presetName ||
                    !selectedBlueprint ||
                    !selectedProvider ||
                    saveConfigMutation.isPending
                  }
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {saveConfigMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Preset'
                  )}
                </button>
              </div>

              {saveConfigMutation.isError && (
                <p className="text-sm text-red-600">
                  {saveConfigMutation.error instanceof Error
                    ? saveConfigMutation.error.message
                    : 'Failed to save preset'}
                </p>
              )}
            </div>
          )}

          {/* Existing Presets */}
          {configsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : printifyConfigs?.configs && printifyConfigs.configs.length > 0 ? (
            <div className="space-y-2">
              {printifyConfigs.configs.map((config: PrintifyConfig) => (
                <div
                  key={config.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{config.name}</span>
                      {config.product_model && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          {config.product_model}
                        </span>
                      )}
                      {config.is_default === 1 && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      ${(config.default_price / 100).toFixed(2)} · {config.product_type}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteConfigMutation.mutate(config.id)}
                    disabled={deleteConfigMutation.isPending}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">
              No presets configured yet. Add one to get started with POD products.
            </p>
          )}
        </div>

        {/* API Keys Info */}
        <div className="card bg-yellow-50 border-yellow-200">
          <h2 className="text-lg font-semibold text-yellow-800 mb-2">API Configuration</h2>
          <p className="text-sm text-yellow-700">
            API keys (OpenRouter, Etsy, Printify) are configured server-side via environment
            variables. Contact your administrator to update API credentials.
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={saveSettingsMutation.isPending || settingsLoading}
          className="btn btn-primary flex items-center gap-2"
        >
          {saveSettingsMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : saveSuccess ? (
            <>
              <Check className="w-4 h-4" />
              Saved!
            </>
          ) : (
            'Save Settings'
          )}
        </button>

        {saveSettingsMutation.isError && (
          <p className="text-sm text-red-600 mt-2">
            {saveSettingsMutation.error instanceof Error
              ? saveSettingsMutation.error.message
              : 'Failed to save settings'}
          </p>
        )}
      </div>
    </div>
  );
}
